import CustomizationRequest from "./request";
import CustomizationStore from "./store";
import * as stores from "./category";
import { imageBaseToUrl, setLogo } from "./lib";

const UpdateFrom = {
  SERVER: "server",
  LOCAL: "local",
};

class Customization {
  constructor() {
    this.isPersisted = !!document.body.dataset.siteBuilderSite;
    // Elements that are not supposed to be replaced or duplicated by turbo streams
    const turboPersistedElements = [
      "[data-controller='site-builder--message']",
      ".calendly-badge-widget",
      ".calendly-overlay",
      ".uwy", // Userway
      "#apexchat_tab_invitation_wrapper",
      "#apexchat_prechat_invitation_wrapper",
      "#apexchat_dompopup_chatwindow_wrapper",
      "#apexchat_exitpopup2_chatwindow_wrapper"
    ];

    // Elements that are supposed to be replaced because they will be added again
    // with next turbo stream. For example scripts injected into head.
    const turboReplaceableElements = ['script[src*="facebook.net/en_US/sdk.js"]', 'style[data-fbcssmodules]'].join(',');
    const persistedElementsSelector = 'body > ' + turboPersistedElements.map(selector => `:not(${selector})`).join('');

    this.elementsToRemove = `${turboReplaceableElements},${persistedElementsSelector}`;
  }

  /*
   * These methods update the data store and sends the update event to the iframe. Do not call these from iframe
   */
  serverUpdate(payload) {
    stores.liquid.update(payload);
    this.#update(UpdateFrom.SERVER, payload);
  }

  localUpdate(payload, action = stores.StoreCategory.MISC) {
    stores[action].update(payload);
    this.#update(UpdateFrom.LOCAL, payload, action);
  }

  #update(type, payload, action) {
    const data = { type, action, updates: payload };
    CustomizationRequest.dispatchPreview(data);
  }

  async pageLoad(page) {
    await this.#serverApply({ url: page });
  }

  reset(options) {
    if (!options) {
      CustomizationStore.clear();
      return;
    }

    if (options.skip_url) {
      const url = stores.liquid.values?.url;
      CustomizationStore.clear();
      stores.liquid.values = { url };
    }
  }

  saveStylesLocally() {
    const iframeDocument = CustomizationRequest.previewIframe();
    let customizationStyles = iframeDocument.getElementById('site-builder-customizations');

    if (!customizationStyles) {
      customizationStyles = document.createElement('style');
      customizationStyles.setAttribute('id', 'site-builder-customizations');

      iframeDocument.getElementById('layout_content').appendChild(customizationStyles);

      customizationStyles.sheet.insertRule(':root {}', 0);
    }

    const rootRuleStyle = customizationStyles.sheet.cssRules.item(':root').style;

    Object.keys(stores.css.colors)
      .forEach(function (colorKey) {
        rootRuleStyle.setProperty(
          `--${colorKey}`,
          CustomizationRequest.getCssVariable(colorKey),
        )
      });
  }

  /*
      Methods that actually apply the changes to iframe based on changes received from parent.
       Do not call them in parent
   */

  async apply(payload) {
    const { type, action, updates } = payload;
    if (type === UpdateFrom.LOCAL) {
      return this.#localApply(updates, action);
    } else {
      return this.#serverApply();
    }
  }

  #localApply(payload, action = stores.StoreCategory.MISC) {
    if (action === stores.StoreCategory.CSS) {
      const imageSet = payload["image-set"];
      let images = {};
      if (imageSet) {
        images = imageBaseToUrl(imageSet);
      }
      return CustomizationRequest.updateCssVariables({ ...payload, ...images });
    }

    if (action === stores.StoreCategory.MISC) {
      let company = payload.company || stores.misc.values.company;

      let tagline = payload.tagline || stores.misc.values.tagline;

      let phone = payload.phone || stores.misc.values.phone;
      if (!phone && !this.isPersisted) {
        phone = "(555) 555-5555";
      }

      const siteNames = document.querySelectorAll(".site-title");

      if (company !== undefined) {
        siteNames.forEach((siteName) => {
          siteName.innerHTML = company;
        });
      }

      const siteTaglines = document.querySelectorAll(".site-tagline");

      /*
        On page reload, if tagline (coming from payload or store) is undefined, we do not want
        it to overwrite the actual tagline text in the layout. Also we want to allow blank strings
        when the tagline text is being removed by the user.
      */
      if (tagline !== undefined) {
        siteTaglines.forEach((taglineText) => {
          taglineText.innerHTML = tagline;
        });
      }

      const validPhoneElements =
        ".phone a, .headerPhone a, a.phone, mobile-phone-bar a";

      const phoneNumbers = document.querySelectorAll(validPhoneElements);

      if (phoneNumbers.length) {
        if (phone) {
          phoneNumbers.forEach((phoneNumber) => {
            const phoneSpan = phoneNumber.querySelector("[data-type='phone']");
            if (phoneSpan) {
              phoneSpan.innerHTML = phone;
            } else {
              phoneNumber.innerHTML = phone;
            }
            phoneNumber?.setAttribute?.("href", `tel:${phone}`);
          });
        } else if (phone === "") {
          phoneNumbers.forEach((phoneNumber) => {
            const phoneSpan = phoneNumber.querySelector("[data-type='phone']");
            if (phoneSpan) {
              phoneSpan.innerHTML = '';
            } else {
              phoneNumber.innerHTML = '';
            }
            phoneNumber?.setAttribute?.("href", '');
          });
        }
      }

      if (payload.logo) {
        const logo = payload.logo;
        const logoURL = logo?.url ?? logo;

        setLogo(logoURL);
      }
    }
  }

  async #serverApply(values = {}) {
    /*
        Cleanup body that has been modified due to integrations
        or custom scripts. All of those changes would be rendered again.
     */
    document.querySelectorAll(this.elementsToRemove).forEach(element => element.remove());
    /*
      In addition to script tags, facebook also uses window.FB to keep track
      of initialized script
    */
    window.FB = undefined;

    await CustomizationRequest.updateLiquidTags({
      ...stores.liquid.values,
      ...values
    });

    document.body.classList.remove("mobile-menu-active");
  }

  async setup() {
    await this.applyLocalChanges();
    await this.#serverApply();
  }

  async persistedSetup() {
    return CustomizationRequest.updateLiquidTags({
      url: stores.liquid.values.url,
    });
  }

  applyLocalChanges() {
    this.#localApply(stores.css.values, stores.StoreCategory.CSS);
    this.#localApply(stores.misc.values);
  }
}

export default new Customization();
